import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2 font-display text-2xl"><Leaf size={20}/> Immortal Vedas</div>
        <div>
          <h2 className="font-display text-5xl italic leading-tight mb-4">"Where there is balance, healing follows."</h2>
          <p className="opacity-70 max-w-md">Sign in to track appointments, manage prescriptions and continue the conversation with your Vaidya.</p>
        </div>
        <div className="text-xs opacity-60">© Immortal Vedas</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="overline mb-2">Welcome back</div>
          <h1 className="font-display text-4xl mb-8">Sign in to your account</h1>
          <div className="space-y-4">
            <GoogleSignInButton />
            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">or with email</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-transparent" />
            </div>
            <Button type="submit" disabled={busy} className="w-full btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              No account? <Link to="/register" className="text-primary underline" data-testid="link-register">Create one</Link>
            </p>
          </div>
        </form>
      </div>
    </main>
  );
}
